/** Retomada de posição com heartbeat (Fase 03 TASK-11).
 *
 * O heartbeat só dispara quando a posição MUDOU e a leitura está ativa — um
 * timer cego geraria escrita constante por aluno parado, e o rate limit da
 * RN-27b é de 30/min por aluno.
 */
import { useCallback, useEffect, useRef } from 'react'

import { gravarPosicaoLocal } from '@/services/localProgress'
import { saveProgress, type ProgressPayload } from '@/services/readerService'

const HEARTBEAT_MS = 10_000

interface UseProgressOptions {
  bookId: string | null
  page: number
  locator: string | null
  scrollPct?: number
  enabled?: boolean
  intervalMs?: number
  save?: (payload: ProgressPayload) => Promise<unknown>
  /** Espelho local para a retomada instantânea (ver `services/localProgress`). */
  saveLocal?: (posicao: {
    bookId: string
    page: number
    scrollPct: number
    locator: string | null
    clientUpdatedAt: string
  }) => Promise<void>
}

export function useProgress({
  bookId,
  page,
  locator,
  scrollPct = 0,
  enabled = true,
  intervalMs = HEARTBEAT_MS,
  save = saveProgress,
  saveLocal = gravarPosicaoLocal,
}: UseProgressOptions) {
  const posicaoAtual = useRef({ page, locator, scrollPct })
  const ultimaEnviada = useRef<string | null>(null)

  // Efeito sem deps: roda depois de TODO render. Escrever a ref durante o
  // render é anti-padrão (o React pode descartar o render), e a batida do
  // heartbeat precisa sempre da posição mais recente.
  useEffect(() => {
    posicaoAtual.current = { page, locator, scrollPct }
  })

  const flush = useCallback(async () => {
    if (!bookId || !enabled) return
    const { page: p, locator: l, scrollPct: s } = posicaoAtual.current
    const assinatura = `${p}|${l ?? ''}|${s.toFixed(2)}`
    if (assinatura === ultimaEnviada.current) return
    ultimaEnviada.current = assinatura
    // O espelho local vai ANTES da rede, e com o mesmo carimbo: é ele que faz a
    // próxima abertura ser instantânea, e ele tem de valer mesmo quando o envio
    // falha — aluno em rede ruim é justamente quem mais sofre com o salto para
    // a página 1.
    const carimbo = new Date().toISOString()
    void saveLocal({
      bookId,
      page: p,
      scrollPct: s,
      locator: l,
      clientUpdatedAt: carimbo,
    })
    try {
      await save({ book_id: bookId, page: p, locator: l, scroll_pct: s })
    } catch {
      // Perder um heartbeat não pode interromper a leitura; a próxima batida
      // reenvia a posição (o upsert é idempotente e last-write-wins).
      ultimaEnviada.current = null
    }
  }, [bookId, enabled, save, saveLocal])

  useEffect(() => {
    if (!bookId || !enabled) return
    const timer = setInterval(() => void flush(), intervalMs)
    return () => {
      clearInterval(timer)
      void flush() // grava a última posição ao sair do livro
    }
  }, [bookId, enabled, intervalMs, flush])

  return { flush }
}
